import type { Cache, FieldArgs } from "@urql/exchange-graphcache";

type CachedQuery = {
  args: FieldArgs;
  relatedCacheKey?: string | null;
  relatedField?: string | null;
};

type CacheOperations = {
  handleCacheUpdate: (cachedQueries: CachedQuery[], prepend?: boolean) => void;
  deleteAllLinks: () => void;
};

export function useCacheOperations({
  cache,
  typename,
  object
}: {
  cache: Cache;
  typename: string;
  object: Record<string, unknown>;
}): CacheOperations {
  const isDeleted = Boolean(object.deleted_at);
  const itemKey = cache.keyOfEntity({
    __typename: typename,
    id: object.journal_id as string
  }) as string;

  function handleCacheUpdate(
    cachedQueries: CachedQuery[] = [],
    prepend = true
  ) {
    if (isDeleted) {
      cachedQueries.forEach(query => removeCacheLink(query));
    } else {
      cachedQueries.forEach(query => addCacheLink(query, prepend));
    }
  }

  function addCacheLink(query: CachedQuery, prepend: boolean) {
    const cacheKey = query.relatedCacheKey || "query_root";
    const queryFieldKey = cache.keyOfField(
      query.relatedField || typename,
      query.args
    );

    if (queryFieldKey) {
      const keys = (cache.resolve(cacheKey, queryFieldKey) || []) as string[];

      if (!keys.includes(itemKey)) {
        if (prepend) {
          cache.link(cacheKey, queryFieldKey, [itemKey, ...keys]);
        } else {
          cache.link(cacheKey, queryFieldKey, [...keys, itemKey]);
        }
      }
    }
  }

  function removeCacheLink(query: CachedQuery) {
    const cacheKey = query.relatedCacheKey || "query_root";
    const queryFieldKey = cache.keyOfField(
      query.relatedField || typename,
      query.args
    );

    if (queryFieldKey) {
      const keys = (cache.resolve(cacheKey, queryFieldKey) || []) as string[];

      if (keys.includes(itemKey)) {
        cache.link(
          cacheKey,
          queryFieldKey,
          keys.filter(key => key !== itemKey)
        );
      }
    }
  }

  function deleteAllLinks() {
    // loop through all queries with given __typename
    // and remove this item from all lists
    cache
      .inspectFields("query_root")
      .filter(field => field.fieldName === typename)
      .forEach(({ fieldKey }) => {
        const keys = (cache.resolve("query_root", fieldKey) || []) as string[];

        cache.link(
          "query_root",
          fieldKey,
          keys.filter(key => key !== itemKey)
        );
      });
  }

  return { handleCacheUpdate, deleteAllLinks };
}

export function resolveNestedList(
  parentId: string,
  cache: Cache,
  parentType: string,
  relatedField: string,
  entityType: string
): Array<unknown> {
  // Need to resolve the related field keys from the parent
  const entityKeys = (cache.resolve(
    { __typename: parentType, id: parentId },
    relatedField
  ) || []) as string[];

  // Create object for each entity with its type and id so cache knows how to link it
  return entityKeys.map(key => ({
    __typename: entityType,
    id: cache.resolve(key, "id")
  }));
}
